import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import { API_BASE_URL } from "../config";
import { Plus, Edit2, Trash2, X, UploadCloud } from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import { compressFilesBatch } from "../utils/imageCompressor";

const formatBytes = (bytes, decimals = 1) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const getTodayIst = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

export default function DailyDarshan() {
    const [darshans, setdarshans] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadedBytesInfo, setUploadedBytesInfo] = useState({ loaded: 0, total: 0 });
    const [compressing, setCompressing] = useState(false);
    const [compressionProgress, setCompressionProgress] = useState(0);
    const [compressionStats, setCompressionStats] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedDarshanForView, setselectedDarshanForView] = useState(null);
    const [currentDarshan, setcurrentDarshan] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        darshan_name: "Daily Darshan",
        darshan_date: "",
        darshan_image_hindi: null,
        darshan_thumbnail_hindi: null,
        darshan_image_english: null,
        darshan_thumbnail_english: null,
        is_quick: false
    });

    // Calculate total media bytes
    const hindiImagesBytes = formData.darshan_image_hindi ? Array.from(formData.darshan_image_hindi).reduce((acc, f) => acc + (f.size || 0), 0) : 0;
    const hindiThumbBytes = formData.darshan_thumbnail_hindi?.size || 0;
    const englishImagesBytes = formData.darshan_image_english ? Array.from(formData.darshan_image_english).reduce((acc, f) => acc + (f.size || 0), 0) : 0;
    const englishThumbBytes = formData.darshan_thumbnail_english?.size || 0;
    const grandTotalBytes = hindiImagesBytes + hindiThumbBytes + englishImagesBytes + englishThumbBytes;

    const fileInputRef = useRef(null);
    const [replaceImageName, setReplaceImageName] = useState(null);

    useEffect(() => {
        fetchdarshans();
        fetchLanguages();
    }, []);

    const fetchLanguages = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/languages/all`);
            const data = await res.json();
            if (data.status && data.data) {
                setLanguages(data.data);
            }
        } catch (err) {
            console.error("Error fetching languages:", err);
        }
    };

    const handleViewImages = (fest) => {
        setselectedDarshanForView(fest);
        setViewModalOpen(true);
    };

    const handleDeleteImage = async (imageName) => {
        if (window.confirm("Are you sure you want to delete this image/video?")) {
            try {
                const response = await fetch(`${API_BASE_URL}/api/daily-darshan/delete-image/${selectedDarshanForView.darshan_id_PK}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ imageName: imageName.split('/').pop() }),
                });

                if (response.ok) {
                    alert("Image deleted successfully");
                    fetchdarshans();
                    const updatedImages = (selectedDarshanForView.darshan_images || []).filter(img => !img.includes(imageName.split('/').pop()));
                    const updatedVideos = (selectedDarshanForView.darshan_videos || []).filter(img => !img.includes(imageName.split('/').pop()));
                    setselectedDarshanForView({ ...selectedDarshanForView, darshan_images: updatedImages, darshan_videos: updatedVideos });
                } else {
                    alert("Failed to delete image");
                }
            } catch (error) {
                console.error("Error deleting image:", error);
                alert("Error deleting image");
            }
        }
    };

    const triggerReplaceImage = (imageName) => {
        setReplaceImageName(imageName.split('/').pop());
        fileInputRef.current.click();
    };

    const handleReplaceFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file || !replaceImageName) return;

        const payload = new FormData();
        payload.append("oldImageName", replaceImageName);
        payload.append("new_image", file);

        Swal.fire({
            title: "Replacing Image...",
            html: `
                <div style="width: 100%; margin-top: 10px; text-align: left;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                        <span id="swal-upload-status" style="font-size: 13px; font-weight: 600; color: #475569;">Uploading replacement...</span>
                        <span id="swal-upload-pct" style="font-size: 16px; font-weight: 800; color: #2563eb;">0%</span>
                    </div>
                    <div style="width: 100%; background: #e2e8f0; height: 14px; border-radius: 9999px; overflow: hidden; position: relative;">
                        <div id="swal-progress-bar" style="width: 0%; height: 100%; background: linear-gradient(90deg, #3b82f6, #06b6d4); border-radius: 9999px; transition: width 0.15s ease;"></div>
                    </div>
                    <div style="margin-top: 8px; font-size: 12px; color: #64748b; display: flex; justify-content: space-between;">
                        <span id="swal-upload-bytes">0 B / ${formatBytes(file.size)}</span>
                        <span style="max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${file.name}</span>
                    </div>
                </div>
            `,
            allowOutsideClick: false,
            showConfirmButton: false
        });

        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/daily-darshan/replace-image/${selectedDarshanForView.darshan_id_PK}`,
                payload,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            const pctEl = document.getElementById("swal-upload-pct");
                            const barEl = document.getElementById("swal-progress-bar");
                            const bytesEl = document.getElementById("swal-upload-bytes");
                            const statusEl = document.getElementById("swal-upload-status");

                            if (pctEl) pctEl.innerText = `${percent}%`;
                            if (barEl) barEl.style.width = `${percent}%`;
                            if (bytesEl) bytesEl.innerText = `${formatBytes(progressEvent.loaded)} / ${formatBytes(progressEvent.total)}`;
                            if (statusEl && percent >= 100) {
                                statusEl.innerText = "Processing replacement on server...";
                            }
                        }
                    }
                }
            );

            Swal.close();
            if (response.data && response.data.status) {
                Swal.fire({
                    icon: "success",
                    title: "Replaced!",
                    text: "Image replaced successfully",
                    timer: 1500,
                    showConfirmButton: false
                });
                fetchdarshans();
                setViewModalOpen(false);
            } else {
                Swal.fire("Error", response.data?.message || "Failed to replace image", "error");
            }
        } catch (error) {
            Swal.close();
            console.error("Error replacing image:", error);
            Swal.fire("Error", error.response?.data?.message || error.message || "Error replacing image", "error");
        }
        e.target.value = null; // Reset input
    };

    const fetchdarshans = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/daily-darshan/all`);
            const data = await res.json();
            if (data.status && data.data) {
                const normalized = [];
                data.data.forEach(item => {
                    const baseItem = {
                        darshan_id_PK: item.darshan_id_PK,
                        darshan_name: item.darshan_name,
                        darshan_date: item.darshan_date,
                        is_quick: item.is_quick
                    };
                    if (item.Hindi && (item.Hindi.darshan_images?.length > 0 || item.Hindi.darshan_videos?.length > 0)) {
                        normalized.push({ ...baseItem, ...item.Hindi, language: 'Hindi' });
                    }
                    if (item.English && (item.English.darshan_images?.length > 0 || item.English.darshan_videos?.length > 0)) {
                        normalized.push({ ...baseItem, ...item.English, language: 'English' });
                    }
                    if (normalized.filter(n => n.darshan_id_PK === item.darshan_id_PK).length === 0) {
                        normalized.push({ ...baseItem, ...item.Hindi, language: 'Hindi' });
                    }
                });
                setdarshans(normalized);
            }
        } catch (err) {
            console.error("Error fetching darshans:", err);
        }
    };

    const handleOpenModal = (darshan = null) => {
        if (darshan) {
            setEditingId(darshan.darshan_id_PK);
            setFormData({
                darshan_name: darshan.darshan_name,
                darshan_date: darshan.darshan_date ? darshan.darshan_date.split('T')[0] : "",
                darshan_image_hindi: null,
                darshan_thumbnail_hindi: null,
                darshan_image_english: null,
                darshan_thumbnail_english: null,
                is_quick: darshan.is_quick === 1
            });
        } else {
            setEditingId(null);
            setFormData({
                darshan_name: "Daily Darshan",
                darshan_date: getTodayIst(),
                darshan_image_hindi: null,
                darshan_thumbnail_hindi: null,
                darshan_image_english: null,
                darshan_thumbnail_english: null,
                is_quick: false
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setUploadProgress(0);
        setCompressionStats(null);
        setCompressing(true);
        setCompressionProgress(0);

        const hindiFiles = formData.darshan_image_hindi ? Array.from(formData.darshan_image_hindi) : [];
        const englishFiles = formData.darshan_image_english ? Array.from(formData.darshan_image_english) : [];
        const hindiThumb = formData.darshan_thumbnail_hindi;
        const englishThumb = formData.darshan_thumbnail_english;

        const itemsToCompress = [
            ...hindiFiles.map(f => ({ file: f, category: 'hindi_img' })),
            ...englishFiles.map(f => ({ file: f, category: 'english_img' })),
            ...(hindiThumb ? [{ file: hindiThumb, category: 'hindi_thumb' }] : []),
            ...(englishThumb ? [{ file: englishThumb, category: 'english_thumb' }] : [])
        ];

        let finalItems = itemsToCompress;
        let compStats = null;
        if (itemsToCompress.length > 0) {
            const result = await compressFilesBatch(itemsToCompress, (pct) => {
                setCompressionProgress(pct);
            });
            finalItems = result.compressedItems;
            compStats = result.stats;
            setCompressionStats(result.stats);
        }
        setCompressing(false);

        const effectiveTotal = compStats?.compressedTotal || grandTotalBytes;
        setUploadedBytesInfo({ loaded: 0, total: effectiveTotal });

        const savedStatsBadge = (compStats && compStats.savedPercent > 0)
            ? `<div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #059669; font-weight: 600; display: flex; justify-content: space-between;"><span>⚡ Compressed: <span style="text-decoration: line-through; color: #94a3b8;">${formatBytes(compStats.originalTotal)}</span> ➔ ${formatBytes(compStats.compressedTotal)}</span><span style="background: #d1fae5; color: #065f46; padding: 2px 6px; border-radius: 4px;">Saved ${compStats.savedPercent}%</span></div>`
            : '';

        Swal.fire({
            title: editingId ? "Updating Daily Darshan..." : "Uploading Daily Darshan Media...",
            html: `
                <div style="width: 100%; margin-top: 10px; text-align: left;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                        <span id="swal-upload-status" style="font-size: 13px; font-weight: 600; color: #475569;">Uploading files...</span>
                        <span id="swal-upload-pct" style="font-size: 18px; font-weight: 800; color: #2563eb;">0%</span>
                    </div>
                    <div style="width: 100%; background: #e2e8f0; height: 16px; border-radius: 9999px; overflow: hidden; position: relative;">
                        <div id="swal-progress-bar" style="width: 0%; height: 100%; background: linear-gradient(90deg, #3b82f6, #06b6d4); border-radius: 9999px; transition: width 0.15s ease;"></div>
                    </div>
                    <div style="margin-top: 10px; font-size: 12px; color: #64748b; display: flex; justify-content: space-between;">
                        <span id="swal-upload-bytes">0 B / ${formatBytes(effectiveTotal)}</span>
                        <span id="swal-upload-hint">Please keep window open</span>
                    </div>
                    ${savedStatsBadge}
                </div>
            `,
            allowOutsideClick: false,
            showConfirmButton: false
        });

        const formPayload = new FormData();
        formPayload.append("darshan_name", formData.darshan_name);
        formPayload.append("darshan_date", formData.darshan_date);
        formPayload.append("is_quick", formData.is_quick ? "1" : "0");

        finalItems.forEach(it => {
            if (it.category === 'hindi_img') formPayload.append("darshan_image_hindi", it.file);
            else if (it.category === 'english_img') formPayload.append("darshan_image_english", it.file);
            else if (it.category === 'hindi_thumb') formPayload.append("darshan_thumbnail_hindi", it.file);
            else if (it.category === 'english_thumb') formPayload.append("darshan_thumbnail_english", it.file);
        });

        try {
            let url = `${API_BASE_URL}/api/daily-darshan/create`;
            let method = "post";
            if (editingId) {
                url = `${API_BASE_URL}/api/daily-darshan/update/${editingId}`;
                method = "put";
            }

            const response = await axios({
                method,
                url,
                data: formPayload,
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const rawPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        const scaledPercent = Math.min(90, Math.round((progressEvent.loaded * 90) / progressEvent.total));
                        setUploadProgress(scaledPercent);
                        setUploadedBytesInfo({ loaded: progressEvent.loaded, total: progressEvent.total });

                        const pctEl = document.getElementById("swal-upload-pct");
                        const barEl = document.getElementById("swal-progress-bar");
                        const bytesEl = document.getElementById("swal-upload-bytes");
                        const statusEl = document.getElementById("swal-upload-status");

                        if (bytesEl) bytesEl.innerText = `${formatBytes(progressEvent.loaded)} / ${formatBytes(progressEvent.total)}`;

                        if (rawPercent < 100) {
                            if (pctEl) pctEl.innerText = `${scaledPercent}%`;
                            if (barEl) barEl.style.width = `${scaledPercent}%`;
                            if (statusEl) statusEl.innerText = `Uploading: ${scaledPercent}%...`;
                        } else {
                            if (barEl) {
                                barEl.style.width = '95%';
                                barEl.style.background = 'linear-gradient(90deg, #3b82f6, #06b6d4, #10b981)';
                            }
                            if (pctEl) pctEl.innerText = 'Saving...';
                            if (statusEl) {
                                statusEl.innerHTML = '<span style="display:inline-flex; align-items:center; gap:6px;"><span class="inline-block w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span> <strong>Optimizing & saving to Cloud...</strong></span>';
                            }
                        }
                    }
                }
            });

            const result = response.data;

            const pctEl = document.getElementById("swal-upload-pct");
            const barEl = document.getElementById("swal-progress-bar");
            const statusEl = document.getElementById("swal-upload-status");
            if (barEl) {
                barEl.style.width = '100%';
                barEl.style.background = '#10b981';
            }
            if (pctEl) pctEl.innerText = '100%';
            if (statusEl) statusEl.innerHTML = '<span style="color:#10b981; font-weight:700;">✅ Upload complete!</span>';
            setUploadProgress(100);

            await new Promise(resolve => setTimeout(resolve, 350));

            Swal.close();
            if (result.status) {
                let successMsg = editingId ? "Daily Darshan updated successfully!" : "Daily Darshan created successfully!";
                if (compStats && compStats.savedPercent > 0) {
                    successMsg += ` (⚡ Compressed: ${formatBytes(compStats.originalTotal)} ➔ ${formatBytes(compStats.compressedTotal)}, Saved ${compStats.savedPercent}%)`;
                }
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: successMsg
                });
                setShowModal(false);
                fetchdarshans();
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: result.message || "Something went wrong!"
                });
            }
        } catch (err) {
            Swal.close();
            console.error(err);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || err.message || "Failed to submit Daily Darshan"
            });
        }
        setCompressing(false);
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this Daily Darshan?")) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/daily-darshan/delete/${id}`, {
                method: "DELETE"
            });
            const data = await res.json();
            if (data.status) {
                fetchdarshans();
            } else {
                alert("Failed to delete");
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
            <Sidebar isOpen={isSidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="flex-1 flex flex-col min-w-0 overflow-hidden lg:pl-64">
                {/* Header */}
                <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 min-h-[64px] h-auto flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 py-3 sm:py-0 gap-3 z-10 sticky top-0">
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        <button
                            onClick={() => setSidebarOpen(!isSidebarOpen)}
                            className="lg:hidden p-2 -ml-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                        <h1 className="text-xl font-bold text-slate-800 dark:text-white truncate">Daily Darshan</h1>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 sm:py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                        <Plus size={18} />
                        Add Daily Darshan
                    </button>
                </header>

                <main className="flex-1 overflow-y-auto p-4 sm:p-6 pb-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {darshans.map(darshan => (
                            <div key={darshan.darshan_id_PK} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
                                <div
                                    className="h-48 bg-slate-100 dark:bg-slate-700 relative cursor-pointer group"
                                    onClick={() => handleViewImages(darshan)}
                                >
                                    {(() => {
                                        const allMedia = [...(darshan.darshan_images || []), ...(darshan.darshan_videos || [])];
                                        if (allMedia.length > 0) {
                                            return allMedia[0].match(/\.(mp4|webm|avi|mpeg|quicktime|mov)$/i) ? (
                                                <video src={allMedia[0]} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" muted loop autoPlay playsInline />
                                            ) : (
                                                <img src={allMedia[0]} alt={darshan.darshan_name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                                            );
                                        }
                                        return <div className="flex items-center justify-center h-full text-slate-400">No Image/Video</div>;
                                    })()}
                                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <span className="bg-white/80 text-black px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm">View All ({((darshan.darshan_images?.length || 0) + (darshan.darshan_videos?.length || 0))})</span>
                                    </div>
                                    {darshan.darshan_thumbnail && (
                                        <div className="absolute bottom-2 left-2 w-10 h-10 rounded-full border-2 border-white overflow-hidden bg-white">
                                            <img src={darshan.darshan_thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                </div>
                                <div className="p-4 flex-1 flex flex-col">
                                    <h3 className="font-semibold text-slate-800 dark:text-white text-lg">{darshan.darshan_name}</h3>
                                    <div className="flex justify-between items-center mt-1 mb-4">
                                        <p className="text-slate-500 dark:text-slate-400 text-sm">
                                            {new Date(darshan.darshan_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </p>
                                        {darshan.language && (
                                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md ${darshan.language === 'English' ? 'bg-emerald-100/80 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300' : 'bg-blue-100/80 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                                                }`}>
                                                {darshan.language}
                                            </span>
                                        )}
                                        {darshan.is_quick === 1 && (
                                            <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                                                Quick Mode
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2 mt-auto">
                                        <button
                                            onClick={() => handleOpenModal(darshan)}
                                            className="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 py-2.5 sm:py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Edit2 size={16} /> Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(darshan.darshan_id_PK)}
                                            className="flex-1 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 py-2.5 sm:py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Trash2 size={16} /> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </main>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4 sm:p-6">
                    <div className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden shadow-xl border border-slate-200 dark:border-slate-700">
                        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center shrink-0">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                                {editingId ? "Edit Daily Darshan" : "Add Daily Darshan"}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-2 -mr-2 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Darshan name *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.darshan_name}
                                    onChange={(e) => setFormData({ ...formData, darshan_name: e.target.value })}
                                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 dark:text-white transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Darshan date *</label>
                                <input
                                    type="date"
                                    required
                                    min={getTodayIst()}
                                    value={formData.darshan_date}
                                    onChange={(e) => setFormData({ ...formData, darshan_date: e.target.value })}
                                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 dark:text-white transition-all"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Hindi Upload */}
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30">
                                    <label className="block text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">
                                        Upload Hindi Variant
                                    </label>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="file"
                                                id="hindiImages"
                                                multiple
                                                className="hidden"
                                                accept="image/*,video/*"
                                                onChange={(e) => setFormData({ ...formData, darshan_image_hindi: e.target.files })}
                                            />
                                            <label
                                                htmlFor="hindiImages"
                                                className="bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-100 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors"
                                            >
                                                Choose Files
                                            </label>
                                            <span className="text-sm text-slate-500">
                                                {formData.darshan_image_hindi ? `${formData.darshan_image_hindi.length} files selected` : 'No file chosen'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            Images/Videos for Hindi language
                                        </p>

                                        <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800/40">
                                            <label className="block text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
                                                Hindi Thumbnail (Optional)
                                            </label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="file"
                                                    id="hindiThumbnail"
                                                    className="hidden"
                                                    accept="image/*"
                                                    onChange={(e) => setFormData({ ...formData, darshan_thumbnail_hindi: e.target.files[0] })}
                                                />
                                                <label
                                                    htmlFor="hindiThumbnail"
                                                    className="bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-100 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors"
                                                >
                                                    Choose File
                                                </label>
                                                <span className="text-sm text-slate-500">
                                                    {formData.darshan_thumbnail_hindi ? formData.darshan_thumbnail_hindi.name : 'No file chosen'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                                                Preferred Size: 1080x1080
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* English Upload */}
                                <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl border border-emerald-100 dark:border-emerald-800/30">
                                    <label className="block text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-3">
                                        Upload English Variant
                                    </label>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="file"
                                                id="englishImages"
                                                multiple
                                                className="hidden"
                                                accept="image/*,video/*"
                                                onChange={(e) => setFormData({ ...formData, darshan_image_english: e.target.files })}
                                            />
                                            <label
                                                htmlFor="englishImages"
                                                className="bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-100 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer hover:bg-emerald-200 dark:hover:bg-emerald-700 transition-colors"
                                            >
                                                Choose Files
                                            </label>
                                            <span className="text-sm text-slate-500">
                                                {formData.darshan_image_english ? `${formData.darshan_image_english.length} files selected` : 'No file chosen'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            Images/Videos for English language
                                        </p>

                                        <div className="mt-4 pt-4 border-t border-emerald-200 dark:border-emerald-800/40">
                                            <label className="block text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-2">
                                                English Thumbnail (Optional)
                                            </label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="file"
                                                    id="englishThumbnail"
                                                    className="hidden"
                                                    accept="image/*"
                                                    onChange={(e) => setFormData({ ...formData, darshan_thumbnail_english: e.target.files[0] })}
                                                />
                                                <label
                                                    htmlFor="englishThumbnail"
                                                    className="bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-100 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer hover:bg-emerald-200 dark:hover:bg-emerald-700 transition-colors"
                                                >
                                                    Choose File
                                                </label>
                                                <span className="text-sm text-slate-500">
                                                    {formData.darshan_thumbnail_english ? formData.darshan_thumbnail_english.name : 'No file chosen'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                                                Preferred Size: 1080x1080
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start sm:items-center gap-3 cursor-pointer select-none" onClick={(e) => { e.preventDefault(); setFormData({ ...formData, is_quick: !formData.is_quick }); }}>
                                <input
                                    type="checkbox"
                                    id="is_quick"
                                    checked={formData.is_quick}
                                    onChange={(e) => setFormData({ ...formData, is_quick: e.target.checked })}
                                    className="w-5 h-5 mt-0.5 sm:mt-0 shrink-0 text-blue-600 bg-slate-100 border-slate-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600 cursor-pointer"
                                />
                                <label htmlFor="is_quick" className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">🚀 Enable Quick Darshan Assignment</label>
                            </div>
                            <div className="sticky bottom-0 bg-white dark:bg-slate-800 pt-2 pb-1 space-y-2">
                                {loading && (
                                    <div className="bg-blue-50 dark:bg-blue-950/40 p-3 rounded-xl border border-blue-200 dark:border-blue-800 space-y-2 shadow-sm text-left">
                                        <div className="flex items-center justify-between text-xs font-semibold">
                                            <span className="text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
                                                {compressing ? (
                                                    <>
                                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
                                                        ⚡ Compressing Images: {compressionProgress}%
                                                    </>
                                                ) : uploadProgress >= 100 ? (
                                                    <>
                                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                                                        Processing on server...
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                                                        Uploading: {uploadProgress}%
                                                    </>
                                                )}
                                            </span>
                                            <span className={`font-bold text-sm ${compressing ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                                {compressing ? `${compressionProgress}%` : `${uploadProgress}%`}
                                            </span>
                                        </div>
                                        <div className="w-full bg-blue-100 dark:bg-blue-900/50 h-3 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all duration-200 ${
                                                    compressing
                                                        ? 'bg-gradient-to-r from-amber-500 via-orange-400 to-emerald-500'
                                                        : uploadProgress >= 100
                                                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                                                        : 'bg-gradient-to-r from-blue-600 to-cyan-500'
                                                }`}
                                                style={{ width: `${compressing ? compressionProgress : uploadProgress}%` }}
                                            />
                                        </div>
                                        <div className="text-[11px] text-slate-500 dark:text-slate-400 flex justify-between items-center">
                                            {compressing ? (
                                                <>
                                                    <span>Optimizing image resolution & size...</span>
                                                    <span>{formatBytes(grandTotalBytes)} original</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>{formatBytes(uploadedBytesInfo.loaded)} uploaded</span>
                                                    <span>{formatBytes(uploadedBytesInfo.total || grandTotalBytes)} total</span>
                                                </>
                                            )}
                                        </div>
                                        {compressionStats && compressionStats.savedPercent > 0 && (
                                            <div className="pt-2 border-t border-blue-200/60 dark:border-blue-800/60 flex items-center justify-between text-xs">
                                                <span className="text-emerald-700 dark:text-emerald-400 font-semibold flex items-center gap-1">
                                                    ⚡ Compressed:
                                                    <span className="line-through text-slate-400 font-normal">{formatBytes(compressionStats.originalTotal)}</span>
                                                    <span>➔</span>
                                                    <span className="font-bold">{formatBytes(compressionStats.compressedTotal)}</span>
                                                </span>
                                                <span className="bg-emerald-100 dark:bg-emerald-950/70 text-emerald-800 dark:text-emerald-300 px-2 py-0.5 rounded-full font-bold text-[11px]">
                                                    Saved {compressionStats.savedPercent}% ({formatBytes(compressionStats.savedBytes)})
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-xl transition duration-200 disabled:opacity-50 shadow-sm flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            <span>
                                                {compressing
                                                    ? `Compressing Images (${compressionProgress}%)...`
                                                    : `Uploading (${uploadProgress}%)...`}
                                            </span>
                                        </>
                                    ) : (
                                        editingId ? "Update Daily Darshan" : "Add Daily Darshan"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* View Images Modal */}
            {viewModalOpen && selectedDarshanForView && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700 p-6 flex items-center justify-between z-10">
                            <div>
                                <h2 className="text-xl font-semibold text-slate-800 dark:text-white">Images for {selectedDarshanForView.darshan_name}</h2>
                                <p className="text-sm text-slate-500">Date: {new Date(selectedDarshanForView.darshan_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                            </div>
                            <button
                                onClick={() => {
                                    setViewModalOpen(false);
                                    setselectedDarshanForView(null);
                                }}
                                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {(() => {
                                const allMedia = [...(selectedDarshanForView.darshan_images || []), ...(selectedDarshanForView.darshan_videos || [])];
                                if (allMedia.length > 0) {
                                    return allMedia.map((imgUrl, index) => {
                                        const isVideo = imgUrl.match(/\.(mp4|webm|avi|mpeg|quicktime|mov)$/i);
                                        return (
                                            <div key={index} className="relative group rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 aspect-[9/16]">
                                                {isVideo ? (
                                                    <video src={imgUrl} controls className="w-full h-full object-cover" />
                                                ) : (
                                                    <img src={imgUrl} alt="darshan" className="w-full h-full object-cover" />
                                                )}
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                                                    <button
                                                        onClick={() => triggerReplaceImage(imgUrl)}
                                                        className="bg-white/20 hover:bg-white/40 p-2 rounded-full text-white backdrop-blur-sm transition-colors"
                                                        title="Replace"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteImage(imgUrl)}
                                                        className="bg-red-500/80 hover:bg-red-500 p-2 rounded-full text-white backdrop-blur-sm transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    });
                                }
                                return <p className="text-slate-500 dark:text-slate-400 col-span-full">No media available for this Darshan.</p>;
                            })()}
                        </div>
                    </div>
                </div>
            )}

            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*,video/*"
                onChange={handleReplaceFileChange}
            />
        </div>
    );
}


